import { getUserInfo } from './auth';
import { getApiV1Url } from '../config/api';

/** 게시판 목록 행 (앱 목록과 동일 필드 구조) */
export interface BoardListItem {
  projectCode: string;
  code: string;
  status: string;
  type: string;
  name: string;
  /** includePostCount=true 조회 시 */
  postCount?: number;
  settings?: unknown;
  note?: string;
  createdBy?: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface BoardListResponse {
  timestamp: number;
  errors?: {
    error?: Array<{
      code: number;
      target: string;
      message: string;
    }>;
  };
  data: {
    totalCount: number;
    boards: BoardListItem[];
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface GetBoardListParams {
  keyword?: string;
  status?: string;
  includePostCount?: boolean;
}

/** 게시판 설정 */
export interface BoardSettings {
  category: boolean;
  reaction: string[];
  view_count: boolean;
  comment: boolean;
  reply: boolean;
  max_reply_depth: number;
  pin: number;
  notify_comment: boolean;
  notify_reply: boolean;
  upload: number;
}

/** POST /projects/{projectCode}/boards 요청 (서버 BoardCreateRequest DTO) */
export interface BoardCreateRequest {
  code: string;
  status: string;
  name: string;
  settings: BoardSettings;
  description?: string;
}

/** POST /projects/{projectCode}/boards/{boardCode} 요청 (서버 BoardUpdateRequest DTO) */
export interface BoardUpdateRequest {
  code: string;
  status: string;
  name: string;
  settings: BoardSettings;
  description?: string;
}

export interface BoardDetail {
  projectCode: string;
  code: string;
  status: string;
  name: string;
  description?: string;
  settings: BoardSettings;
  createdBy?: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface BoardDetailResponse {
  timestamp: number;
  errors?: {
    error?: Array<{
      code: number;
      target: string;
      message: string;
    }>;
  };
  data: BoardDetail & { settings?: unknown };
}

/** API settings(JsonNode) → BoardSettings */
export function parseBoardSettings(raw: unknown): BoardSettings {
  let value: unknown = raw;
  if (typeof value === 'string' && value.trim() !== '') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      value = {};
    }
  }
  const s =
    value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  const reactionRaw = s.reaction;
  const reaction = Array.isArray(reactionRaw)
    ? reactionRaw.filter((x): x is string => typeof x === 'string')
    : [];

  return {
    category: Boolean(s.category),
    reaction,
    view_count: Boolean(s.view_count),
    comment: Boolean(s.comment),
    reply: Boolean(s.reply),
    max_reply_depth: Number(s.max_reply_depth) || 0,
    pin: Number(s.pin) || 0,
    notify_comment: Boolean(s.notify_comment),
    notify_reply: Boolean(s.notify_reply),
    upload: Number(s.upload) || 0,
  };
}

/** 목록 settings에서 카테고리 사용 여부 */
export function isBoardCategoryEnabled(settings: unknown): boolean {
  return parseBoardSettings(settings).category;
}

/** 게시판 카테고리 설정 화면 경로 */
export function getBoardCategoryConfigPath(projectCode: string, boardCode: string): string {
  return `/projects/${projectCode}/boards/${encodeURIComponent(boardCode)}/categories`;
}

export const DEFAULT_BOARD_SETTINGS: BoardSettings = {
  category: false,
  reaction: [],
  view_count: false,
  comment: false,
  reply: false,
  max_reply_depth: 0,
  pin: 0,
  notify_comment: false,
  notify_reply: false,
  upload: 0,
};

/**
 * 게시판 목록 조회
 * GET /api/v1/projects/{projectCode}/boards?size=0&page=0
 */
export const getBoardList = async (
  projectCode: string,
  params?: GetBoardListParams
): Promise<{
  boards: BoardListItem[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}> => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }

  const queryParams = new URLSearchParams({
    size: '0',
    page: '0',
  });
  if (params?.keyword) queryParams.append('keyword', params.keyword);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.includePostCount) {
    queryParams.append('includePostCount', 'true');
  }

  const response = await fetch(
    getApiV1Url(`/projects/${projectCode}/boards?${queryParams}`),
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Account-Code': userInfo.accountCode,
        'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const result: BoardListResponse = await response.json();

  if (result.errors?.error && result.errors.error.length > 0) {
    const errorMessage = result.errors.error.map((e) => e.message).join(', ');
    throw new Error(errorMessage);
  }

  const boards = (result.data.boards ?? []).map((board) => ({
    ...board,
    postCount:
      board.postCount != null
        ? Number(board.postCount) || 0
        : undefined,
    settings: board.settings != null ? parseBoardSettings(board.settings) : board.settings,
  }));

  return {
    boards,
    totalCount: result.data.totalCount ?? 0,
    totalPages: result.data.totalPages ?? 0,
    currentPage: result.data.currentPage ?? 0,
    pageSize: result.data.pageSize ?? 0,
    hasNext: result.data.hasNext ?? false,
    hasPrevious: result.data.hasPrevious ?? false,
  };
};

/**
 * 게시판 생성
 * POST /api/v1/projects/{projectCode}/boards
 */
export const createBoard = async (
  projectCode: string,
  body: BoardCreateRequest
): Promise<{ success: boolean; data?: BoardListItem }> => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }

  const response = await fetch(getApiV1Url(`/projects/${projectCode}/boards`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
    },
    body: JSON.stringify({
      code: body.code.trim(),
      status: body.status,
      name: body.name.trim(),
      settings: body.settings,
      ...(body.description != null && body.description !== ''
        ? { description: body.description.trim() }
        : {}),
    }),
  });

  const result = await response.json().catch(() => ({}));

  if (result.errors?.error && result.errors.error.length > 0) {
    const errorMessage = result.errors.error.map((e: { message?: string }) => e.message).join(', ');
    throw new Error(errorMessage);
  }

  if (!response.ok) {
    throw new Error(result.message || `HTTP error! status: ${response.status}`);
  }

  return { success: true, data: result.data };
};

/**
 * 게시판 삭제
 * DELETE /api/v1/projects/{projectCode}/boards/{boardCode}
 */
export const deleteBoard = async (
  projectCode: string,
  boardCode: string
): Promise<{ success: boolean; message: string }> => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }

  const response = await fetch(
    getApiV1Url(`/projects/${projectCode}/boards/${encodeURIComponent(boardCode)}`),
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-Account-Code': userInfo.accountCode,
        'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
      },
    }
  );

  const result = await response.json().catch(() => ({}));

  if (result.errors?.error && result.errors.error.length > 0) {
    const errorMessage = result.errors.error.map((e: { message?: string }) => e.message).join(', ');
    throw new Error(errorMessage);
  }

  if (!response.ok) {
    throw new Error(result.message || `HTTP error! status: ${response.status}`);
  }

  return { success: true, message: '게시판이 삭제되었습니다.' };
};

function getBoardAuthHeaders(): Record<string, string> {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }
  return {
    'Content-Type': 'application/json',
    'X-Account-Code': userInfo.accountCode,
    'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
  };
}

/**
 * 게시판 상세 조회
 * GET /api/v1/projects/{projectCode}/boards/{boardCode}
 */
export const getBoardDetail = async (
  projectCode: string,
  boardCode: string
): Promise<BoardDetail | null> => {
  const response = await fetch(
    getApiV1Url(`/projects/${projectCode}/boards/${encodeURIComponent(boardCode)}`),
    { method: 'GET', headers: getBoardAuthHeaders() }
  );

  const result: BoardDetailResponse = await response.json().catch(() => ({}));

  if (result.errors?.error && result.errors.error.length > 0) {
    const errorMessage = result.errors.error.map((e) => e.message).join(', ');
    throw new Error(errorMessage);
  }

  if (!response.ok) {
    throw new Error((result as { message?: string }).message || `HTTP error! status: ${response.status}`);
  }

  if (!result.data) return null;

  const { settings: rawSettings, ...rest } = result.data;
  return {
    ...rest,
    settings: parseBoardSettings(rawSettings),
  };
};

/**
 * 게시판 수정
 * POST /api/v1/projects/{projectCode}/boards/{boardCode}
 */
export const updateBoard = async (
  projectCode: string,
  boardCode: string,
  body: BoardUpdateRequest
): Promise<{ success: boolean; data?: BoardDetail }> => {
  const response = await fetch(
    getApiV1Url(`/projects/${projectCode}/boards/${encodeURIComponent(boardCode)}`),
    {
      method: 'POST',
      headers: getBoardAuthHeaders(),
      body: JSON.stringify({
        code: body.code.trim(),
        status: body.status,
        name: body.name.trim(),
        settings: body.settings,
        ...(body.description != null && body.description !== ''
          ? { description: body.description.trim() }
          : {}),
      }),
    }
  );

  const result = await response.json().catch(() => ({}));

  if (result.errors?.error && result.errors.error.length > 0) {
    const errorMessage = result.errors.error.map((e: { message?: string }) => e.message).join(', ');
    throw new Error(errorMessage);
  }

  if (!response.ok) {
    throw new Error(result.message || `HTTP error! status: ${response.status}`);
  }

  return { success: true, data: result.data };
};
